import React from 'react';
import classNames from 'classnames';
import { STANDARD_ARROW_SPACING, VALID_DIRECTIONS, VALID_STYLES } from './constants';
import { getLocation } from './utils';
import styles from './Tooltip.module.scss';

interface Props {
    tooltipId?: string;
    children: React.ReactNode;
    targetNode?: HTMLElement | null;
    arrowLocation?: (typeof VALID_DIRECTIONS)[number];
    style?: (typeof VALID_STYLES)[number];
}

const ANIMATION_MAP = {
    left: styles.tooltipFadeInLeft,
    right: styles.tooltipFadeInRight,
    top: styles.tooltipFadeInDown,
    bottom: styles.tooltipFadeInUp,
};

const _updatePosition = (initialLocation, finalArrowLocation) => {
    const verticalVals = ['bottom', 'top'];
    const negativeVals = ['bottom', 'right'];
    let key = 'left';
    let spacing = STANDARD_ARROW_SPACING;

    if (verticalVals.indexOf(finalArrowLocation) > -1) {
        key = 'top';
    }

    if (negativeVals.indexOf(finalArrowLocation) > -1) {
        spacing = -spacing;
    }

    const adjustedValue = initialLocation[key] + spacing;

    return {
        ...initialLocation,
        [key]: adjustedValue,
    };
};

export class Tooltip extends React.PureComponent<Props> {
    _toolTipNode: HTMLElement | null = null;

    state = {
        targetNode: null as HTMLElement | null,
        height: -1,
        width: -1,
    };

    static defaultProps = {
        arrowLocation: 'top',
        style: 'basic',
    };

    constructor(props) {
        super(props);

        this.state = {
            targetNode: props.targetNode,
            height: -1,
            width: -1,
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps: Readonly<Props>) {
        this.setState({
            targetNode: nextProps.targetNode,
        });
    }

    componentDidUpdate() {
        if (this._toolTipNode && !this._componentIsRendered()) {
            /* Needed to trigger a re-render once the component has actually
            been mounted and we have access to its height and width */
            this.setState({
                height: this._toolTipNode.offsetHeight,
                width: this._toolTipNode.offsetWidth,
            });
        }
    }

    _setToolTipNode(node) {
        if (node) {
            this._toolTipNode = node;
        }
    }

    _componentIsRendered() {
        return this.state.height > -1 && this.state.width > -1;
    }

    render() {
        if (!this.state.targetNode) {
            return null;
        }

        let finalLocation;
        const { children, arrowLocation, style, tooltipId } = this.props;
        const { targetNode, height, width } = this.state;

        if (this._componentIsRendered()) {
            const initialLocation = getLocation(targetNode, arrowLocation, width, height);

            finalLocation = _updatePosition(initialLocation, arrowLocation);
        }

        const className = classNames(
            styles.tooltip,
            styles[`tooltipArrow-${arrowLocation}`],
            styles[`tooltip-${style}`],
            {
                [arrowLocation ? ANIMATION_MAP[arrowLocation] : '']: finalLocation,
            },
        );

        return (
            <div
                id={tooltipId}
                className={className}
                style={finalLocation}
                ref={this._setToolTipNode.bind(this)}
                data-spec="tooltip"
                role="tooltip"
            >
                {children}
            </div>
        );
    }
}
